import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, type Column } from "@legal-500/legal500-design-system";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import {
  bulkReferenceRuns,
  type BulkReferenceRun,
  type BulkReferenceRunDetail,
} from "../../../api/addressBook.service.js";
import { fromRunRows } from "./reportRun.js";
import { downloadResultsCsv } from "./resultsExport.js";
import { ResultsTable, type ResultsFilter } from "./ResultsTable.js";
import styles from "./BulkExternalRefs.module.css";

/**
 * Past runs, so an upload can be reviewed by someone who did not perform it.
 *
 * Reading is gated on addressbook.read rather than write, which is the point: the
 * product team can look at what a load did without being able to load anything. The
 * page renders this on its own for a reader — see the canWrite branch in index.tsx.
 */

const RUNS_LIMIT = 25;

type RunTableRow = {
  id: string;
  when: string;
  file: string;
  who: string;
  outcome: string;
  status: BulkReferenceRun["status"];
};

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function PreviousRuns() {
  const [runs, setRuns] = useState<BulkReferenceRun[]>([]);
  const [selected, setSelected] = useState<BulkReferenceRunDetail | null>(null);
  const [filter, setFilter] = useState<ResultsFilter>("all");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await bulkReferenceRuns.list(RUNS_LIMIT, 0);
      setRuns(response.data);
    } catch (listError) {
      setError(listError instanceof Error ? listError.message : String(listError));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openRun = useCallback(async (id: string) => {
    setError(null);
    try {
      const response = await bulkReferenceRuns.get(id);
      setSelected(response.data);
      setFilter("all");
    } catch (getError) {
      setError(getError instanceof Error ? getError.message : String(getError));
    }
  }, []);

  // Memoised because a filter click re-renders this component, and rebuilding several
  // thousand rows per click is wasted work — the run itself never changes while open.
  const selectedResults = useMemo(() => (selected ? fromRunRows(selected.rows) : []), [selected]);

  const rows: RunTableRow[] = runs.map(run => ({
    id: run.id,
    when: formatWhen(run.startedAt),
    file: run.fileName ?? "—",
    who: run.startedByEmail ?? "—",
    outcome:
      run.status === "running"
        ? `${run.createdCount + run.skippedCount + run.failedCount} of ${run.totalRows} — no final report`
        : `${run.createdCount} created · ${run.skippedCount} skipped · ${run.failedCount} failed`,
    status: run.status,
  }));

  const columns: Column<RunTableRow>[] = [
    { header: "When", accessor: "when" },
    { header: "File", accessor: "file", wrap: true },
    { header: "Run by", accessor: "who" },
    { header: "Outcome", accessor: "outcome", wrap: true },
    {
      header: "",
      accessor: "id",
      render: (_value, row) => (
        <Button variant="outlined" onClick={() => void openRun(row.id)}>
          View
        </Button>
      ),
    },
  ];

  if (selected) {
    return (
      <div className={styles.stack}>
        <div className={styles.toolbar}>
          <Button variant="outlined" onClick={() => setSelected(null)}>
            Back to previous runs
          </Button>
          <span className={styles.spacer} />
          <span className={styles.resultCount}>
            {selected.fileName ?? "Untitled"} · {formatWhen(selected.startedAt)}
            {selected.startedByEmail ? ` · ${selected.startedByEmail}` : ""}
          </span>
        </div>

        {/* Deliberately does not say the run was abandoned. `running` only means no
            final report has arrived, which is equally true of an upload happening right
            now — and the response carries no heartbeat to tell the two apart. */}
        {selected.status === "running" ? (
          <div className={`${styles.banner} ${styles.bannerWarning}`}>
            No final report for this run. It may still be going, or the tab that started it may have
            gone away. What you see below is everything recorded at the last checkpoint.
          </div>
        ) : null}

        <ResultsTable
          results={selectedResults}
          filter={filter}
          onFilterChange={setFilter}
          // Stamped with the run's start time, not now, so the file is named after the
          // run it came from. A reader cannot re-run an upload, so this is the only
          // thing they can do with a run besides look at it.
          onExport={() =>
            downloadResultsCsv(
              selectedResults,
              selected.referenceType,
              new Date(selected.startedAt),
            )
          }
          onRetryFailed={() => {
            /* Retrying a historical run is not offered — re-upload the file instead. */
          }}
          isBusy
          // The run's own type, not the page's current selection, so an older run
          // still reads correctly after someone changes the dropdown.
          referenceType={selected.referenceType}
        />
      </div>
    );
  }

  return (
    <div className={styles.stack}>
      <div className={styles.toolbar}>
        <div className={styles.cardTitle}>Previous runs</div>
        <span className={styles.spacer} />
        <Button variant="outlined" onClick={() => void load()} disabled={isLoading}>
          Refresh
        </Button>
      </div>

      {error ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          {error}
        </div>
      ) : null}

      <DataTable data={rows} columns={columns} emptyMessage="No uploads have been recorded yet." />
    </div>
  );
}
